import { PlateList } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUserOrganization } from '@paragon/login-tools';

import { getMaterialSetPrototype } from '../selectors/plateListSelectors';
import { UserVisibleError } from '../UserVisibleError';
import { receiveMaterialListEditResponse } from './receiveMaterialListEditResponse';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActionTypes';

const {
  materialLists: { upsertPlateList },
} = designService;

export const createPlateList =
  (name: string): AsyncThunkActionReturn<string> =>
  async (dispatch) => {
    const guid = crypto.randomUUID();
    await dispatch(upsertPlateListPrototype({ guid, name, isArchived: false, plates: [], useComboPlates: false }));

    return guid;
  };

export const upsertPlateListPrototype =
  (updatedPrototype: PlateList): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const organization = getLoggedInUserOrganization(state);

    if (organization == null) throw new Error('Unauthenticated user attempted to edit plate list.');

    const result = await upsertPlateList(organization, updatedPrototype);
    dispatch(receiveMaterialListEditResponse(result));
  };

export const duplicatePlateList =
  (sourceGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const materialSetPrototype = getMaterialSetPrototype(getState());
    if (materialSetPrototype.status !== 'loaded') {
      throw new UserVisibleError('Plate lists are not loaded. Unable to duplicate.');
    }

    const sourceList = materialSetPrototype.plateLists[sourceGuid];
    if (sourceList == null) {
      throw new UserVisibleError('That plate list does not exist. Unable to duplicate.');
    }

    const duplicatedListGuid = crypto.randomUUID();
    const duplicatedList = {
      ...sourceList,
      guid: duplicatedListGuid,
      name: `Copy of ${sourceList.name}`,
    };

    await dispatch(upsertPlateListPrototype(duplicatedList));
  };

export const renamePlateList =
  (plateListGuid: string, newName: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = getLoggedInUserOrganization(getState());
    if (organization == null) throw new Error('Unauthenticated user attempted to rename plate list.');

    const response = await designService.materialLists.renamePlateList(organization, plateListGuid, newName);
    dispatch(receiveMaterialListEditResponse(response));
  };

export const archivePlateList =
  (guid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = getLoggedInUserOrganization(getState());
    if (organization == null) throw new Error('Unauthenticated user attempted to delete plate list.');

    const response = await designService.materialLists.archivePlateList(organization, guid);
    dispatch(receiveMaterialListEditResponse(response));
  };

export const restorePlateList =
  (guid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = getLoggedInUserOrganization(getState());
    if (organization == null) throw new Error('Unauthenticated user attempted to restore plate list.');

    const response = await designService.materialLists.restorePlateList(organization, guid);
    dispatch(receiveMaterialListEditResponse(response));
  };
